import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminUsers } from '@/api/(admin)/users';
import type { AdminUser } from '@/api/(admin)/users/types';
import type { AdminUserListRow, AdminUsersQueryInput } from '../-types';
import { buildAdminUsersQs, toAdminUsersParams } from '../-utils/listParams';

/**
 * Shared by the route loader and the component's hook.
 *
 * The key is the *converted* params rather than the raw input, so the loader's
 * URL-shaped values and `useTable`'s state-shaped values land on the same cache
 * entry — otherwise the prefetch warms a key nothing reads and the page still
 * flashes a spinner.
 */
export function adminUsersQueryOptions(input: AdminUsersQueryInput) {
  const params = toAdminUsersParams(input);
  return queryOptions({
    queryKey: ['admin', 'users', 'list', params] as const,
    queryFn: () => fetchAdminUsers(buildAdminUsersQs(params)),
    staleTime: 60_000,
  });
}

/** Everything under this prefix — what the block mutations invalidate. */
export const adminUsersQueryKey = ['admin', 'users'] as const;

function toRow(user: AdminUser): AdminUserListRow {
  return {
    ...user,
    rowKey: user.id,
    fullName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
  };
}

export function useAdminUsers(input: AdminUsersQueryInput) {
  const query = useQuery(adminUsersQueryOptions(input));

  return {
    ...query,
    rows: query.data?.data.map(toRow) ?? [],
    totalCount: query.data?.meta.totalCount ?? 0,
  };
}
